// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import {
  BaseColorSelect,
  BaseInput,
  BaseInputPhone,
  BaseSelect,
  FileUpload,
  Loading,
} from '~/components/common';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { authRouteAbsolute } from '~/utils/routes/auth';
import { mockDataEmployeeRole } from '~/utils/mockData/employee';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createEmployee, getEmployeeDetail, updateEmployee } from '~/thunks/employee/employeeThunk';
import { getProfile } from '~/thunks/user/userThunk';
import { IBaseOption, IFileUpload } from '~/utils/interface/common';
import { IBodyUpdateEmployee, IFormEmployee } from '~/utils/interface/employee';
import {
  DEFAULT_NUMBER_ZERO,
  employeeColors,
  EMPTY_STRING,
  FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE,
} from '~/utils/constants/common';
import { defaultValuesForm, schema } from '~/pages/template/formEmployee/helper';
import {
  compareDataUpdate,
  createFormData,
  formatInputDecimal,
  formatPhoneNumber,
  isEmptyObject,
  removeEmptyObjects,
  removeNonDigits,
} from '~/utils/helper/common';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { staffRouteAbsolute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './FormEmployee.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FormEmployee = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/employee', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: employeeId } = useParams();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<IFormEmployee>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const roleWatch = watch('role');
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormEmployee>(defaultValuesForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!employeeId) return;

    handleGetEmployeeDetail(employeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    if (roleWatch !== RoleEnum.STAFF && roleWatch !== RoleEnum.TECHNICIAN) {
      setValue('hourlyRate', EMPTY_STRING);
      setValue('commissionRate', EMPTY_STRING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleWatch]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetEmployeeDetail = (employeeId: string) => {
    setIsLoading(true);

    dispatch(getEmployeeDetail(employeeId))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, avatarImage, phoneNumber, ...remainingData } = res?.data;
        const body: IFormEmployee = {
          ...remainingData,
          phoneNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : EMPTY_STRING,
          avatarImage: avatarImage ? { url: avatarImage } : {},
        };

        setInitialData(body);
        reset(body);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancelFormEmployee = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.employees);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.employees);
        break;
    }

    reset(defaultValuesForm);
  };

  const handleSubmitFormEmployee = (data: IFormEmployee) => {
    if (employeeId) {
      handleEditEmployee(data);
    } else {
      handleAddEmployee(data);
    }
  };

  const handleAddEmployee = async (data: IFormEmployee) => {
    setIsLoading(true);

    const newData: IFormEmployee = {
      ...data,
      phoneNumber: removeNonDigits(data?.phoneNumber),
    };

    const formData = await createFormData(removeEmptyObjects(newData), 'avatarImage');

    dispatch(createEmployee(formData))
      .unwrap()
      .then((res) => {
        handleCancelFormEmployee();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditEmployee = async (data: IFormEmployee) => {
    setIsLoading(true);

    const dataChange = compareDataUpdate(initialData, data) as IFormEmployee;

    const isDefaultAvatar = initialData?.avatarImage?.url?.includes('avatar_default');
    const colorChanged = !!dataChange.displayColor;

    const formatData: IFormEmployee = {
      ...dataChange,
      ...(dataChange.avatarImage && isEmptyObject(dataChange.avatarImage)
        ? { avatarImage: undefined, deleteAvatar: true }
        : colorChanged && isDefaultAvatar
        ? { avatarImage: undefined, deleteAvatar: true }
        : { avatarImage: dataChange.avatarImage }),
      phoneNumber: dataChange?.phoneNumber && removeNonDigits(data?.phoneNumber),
    };

    if (formatData.role === RoleEnum.ADMIN) {
      delete formatData.hourlyRate;
      delete formatData.commissionRate;
    }

    const formData = await createFormData(formatData, 'avatarImage');

    const params: IBodyUpdateEmployee = {
      id: employeeId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateEmployee(params))
      .unwrap()
      .then(() => {
        const currentAccountId = localStorage.getItem(StorageEnum.ACCOUNT_ID);
        if (employeeId && currentAccountId && employeeId === currentAccountId) {
          const currentStoredRole = localStorage.getItem(StorageEnum.ROLE);
          if (data.role !== currentStoredRole) {
            // Own role changed — force re-login to keep localStorage.ROLE,
            // ProtectedRoute, Sidebar, and all 61+ role consumers consistent.
            localStorage.clear();
            navigate(authRouteAbsolute.login, { replace: true });
          } else {
            // Only non-role fields changed — silently refresh profile in header.
            dispatch(getProfile());
            handleCancelFormEmployee();
          }
        } else {
          handleCancelFormEmployee();
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const dataOptionRole =
    currentRole === RoleEnum.STAFF
      ? mockDataEmployeeRole.filter((option) => option.value !== RoleEnum.ADMIN)
      : mockDataEmployeeRole;
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formEmployeePage'
      title={t(employeeId ? 'form_employee_edit_title' : 'form_employee_add_title')}
      onBackToPreviousPage={handleCancelFormEmployee}
    >
      <Stack
        component={'form'}
        spacing={'20px'}
        onSubmit={handleSubmit(handleSubmitFormEmployee)}
        className={cx('form')}
      >
        <Box component={'div'} className={cx('formBody')}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={12}>
              <Controller
                name='avatarImage'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FileUpload
                    accept={FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE}
                    label={t('form_employee_label_profile_image')}
                    {...(employeeId && !value?.file && { uploadedFiles: value?.url })}
                    onChange={(files: IFileUpload[]) => onChange(files?.[DEFAULT_NUMBER_ZERO])}
                    errorMessage={errors.avatarImage?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='firstName'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      required
                      label={t('form_employee_label_first_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.firstName?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='lastName'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      required
                      label={t('form_employee_label_last_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.lastName?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='email'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      required
                      label={t('form_employee_label_email')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.email?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='phoneNumber'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInputPhone
                      label={t('form_employee_label_phone_number')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.phoneNumber?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='role'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      label={t('form_employee_label_role')}
                      value={value}
                      options={dataOptionRole || []}
                      onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                      required
                      errorMessage={errors.role?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='displayColor'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseColorSelect
                      label={t('form_employee_label_colors')}
                      colors={employeeColors}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.displayColor?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            {currentRole === RoleEnum.ADMIN &&
              (roleWatch === RoleEnum.STAFF || roleWatch === RoleEnum.TECHNICIAN) && (
                <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                  <Grid2 size={6}>
                    <Controller
                      name='hourlyRate'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_employee_label_hourly_rate')}
                          value={value}
                          onChange={(event) => {
                            onChange(formatInputDecimal(event.target.value));
                          }}
                          errorMessage={errors?.hourlyRate?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <Controller
                      name='commissionRate'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_employee_label_commission_rate')}
                          value={value}
                          onChange={(event) => {
                            onChange(formatInputDecimal(event.target.value));
                          }}
                          errorMessage={errors?.commissionRate?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              )}
          </Grid2>
        </Box>

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={handleCancelFormEmployee}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={employeeId ? !isDirty : false}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>

        {isLoading && <Loading />}
      </Stack>
    </ChildrenPage>
  );
};

export default FormEmployee;
